import React, { useState, useEffect } from 'react';
import { getBookmarkExists, deleteBookmark, saveBookmark, saveExamResult } from '../services/SimuladoEngineRepository';
import { auth } from '../shared/api/firebase/config'; 
import { FIREBASE_CONFIG } from '../config/Config';
import { QuestionLoader } from '../utils/QuestionLoader';
import { ACADEMIA_CONSTANTS } from '../features/academy/AcademiaConstants';
import AdvancedFilterBar from './AdvancedFilterBar';
import { ArrowLeft, Bookmark, Check, X, ChevronRight, ChevronLeft, Lightbulb, BookOpen, Scale, AlertTriangle } from 'lucide-react';

const appId = FIREBASE_CONFIG.projectId;

const SimuladoEngine = ({ onExit, filters = {} }) => {
    const [questions, setQuestions] = useState([]);
    const [activeFilters, setActiveFilters] = useState(filters);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({}); // { questionId: selectedIndex }
    const [selectedOption, setSelectedOption] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [loading, setLoading] = useState(true);
    const [score, setScore] = useState(null); // null = em andamento
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

    // 1. CARREGAMENTO INTELIGENTE
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                // Mescla filtros iniciais com interações do usuário
                const queryFilters = {
                    limit: 50,
                    shuffle: true,
                    ...activeFilters 
                };
                
                const q = await QuestionLoader.getQuestions(queryFilters);
                setQuestions(q);
                
                // Reset de Estado
                setCurrentIndex(0);
                setUserAnswers({});
                setSelectedOption(null);
                setScore(null);
                setShowFeedback(false);
            } catch (e) {
                console.error("Erro ao carregar simulado:", e);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [activeFilters]);

    const handleFilterUpdate = (newFilters) => {
        setActiveFilters(prev => ({ ...prev, ...newFilters }));
    };

    // 2. GESTÃO DE BOOKMARKS (CADERNO DE ERROS)
    useEffect(() => {
        const checkBookmark = async () => {
            if (!questions[currentIndex] || !user) return;
            const qId = questions[currentIndex].id;
            try {
                const exists = await getBookmarkExists(user.uid, qId, appId);
                setIsBookmarked(exists);
            } catch (e) { console.error(e); }
        };
        checkBookmark();
    }, [currentIndex, questions]);

    const toggleBookmark = async () => {
        if (!user) return alert("Faça login para salvar no caderno.");
        const qId = questions[currentIndex].id;
        try {
            if (isBookmarked) {
                await deleteBookmark(user.uid, qId, appId);
                setIsBookmarked(false);
            } else {
                await saveBookmark(user.uid, qId, {
                    questionId: qId,
                    materia: questions[currentIndex].materia
                }, appId);
                setIsBookmarked(true);
            }
        } catch (e) { console.error("Erro ao salvar bookmark:", e); }
    };

    // 3. LÓGICA DE RESPOSTA
    const handleAnswer = (optionIndex) => {
        if (showFeedback || score !== null) return; // Trava cliques duplos
        setSelectedOption(optionIndex);
    };

    const confirmAnswer = () =>
 {
        if (showFeedback || score !== null) return;
        const currentQ = questions[currentIndex];
        if (selectedOption === null || selectedOption === undefined) return;
        setUserAnswers(prev => ({ ...prev, [currentQ.id]: selectedOption }));
        setShowFeedback(true);
    };

    const nextQuestion = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setShowFeedback(false);
            setSelectedOption(null);
            // Scroll suave para o topo
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            finishExam();
        }
    };

    // 4. FINALIZAÇÃO E ANALYTICS
    const finishExam = async () => {
        let hits = 0;
        const details = {}; // Detalhamento por matéria para o Radar Estratégico

        questions.forEach(q => {
            const isCorrect = userAnswers[q.id] === q.correta;
            if (isCorrect) hits++;
            
            // Agregação de estatísticas
            if (!details[q.materia]) details[q.materia] = { total: 0, hits: 0 };
            details[q.materia].total++;
            if (isCorrect) details[q.materia].hits++;
        });
        
        setScore(hits);

        if (user) {
            try {
                const percentage = Math.round((hits / questions.length) * 100);
                await saveExamResult(user.uid, {
                    score: hits,
                    total: questions.length,
                    percentage: percentage,
                    details: details,
                    filters: activeFilters,
                }, appId);
            } catch (e) { console.error("Erro ao salvar resultados:", e); }
        }
    };

    // --- RENDERIZAÇÃO ---

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-96 animate-pulse">
            <i className="fas fa-circle-notch fa-spin text-4xl text-[#C5A059] mb-4"></i>
            <p className="text-gray-500 font-bold text-sm uppercase tracking-widest">Calibrando Motor...</p>
        </div>
    );

    if (questions.length === 0) return (
        <div className="max-w-5xl mx-auto pb-20 animate-fade-in">
             <div className="flex justify-between items-center mb-6">
                <button onClick={onExit} className="text-gray-400 hover:text-red-500 text-sm font-bold flex items-center gap-2 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> VOLTAR AO PAINEL
                </button>
            </div>
            <AdvancedFilterBar onFilterChange={handleFilterUpdate} totalResults={0} />
            <div className="text-center p-20 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl">
                <i className="fas fa-search text-4xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-bold text-gray-600">Nenhuma questão encontrada</h3>
                <p className="text-sm text-gray-500 mt-2">Tente suavizar os filtros ou selecionar outra disciplina.</p>
            </div>
        </div>
    );

    // TELA DE RESULTADO FINAL
    if (score !== null) {
        const percentage = Math.round((score / questions.length) * 100);
        return (
            <div className="max-w-2xl mx-auto bg-white p-10 rounded-2xl shadow-xl border-t-8 border-[#C5A059] animate-fade-in-up text-center pb-12 mt-10">
                <h2 className="text-2xl font-serif font-bold text-[#20243B] mb-2">Simulado Finalizado</h2>
                <p className="text-gray-500 text-sm">Seus dados foram processados pelo algoritmo.</p>
                
                <div className="my-10 relative inline-block">
                    <div className="text-8xl font-black text-[#C5A059] tracking-tighter">{percentage}%</div>
                    <div className="text-gray-400 text-xs font-bold uppercase tracking-[0.2em] mt-2 border-t border-gray-100 pt-2">Aproveitamento</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto mb-10">
                    <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                        <p className="text-[10px] uppercase font-bold text-green-700">Acertos</p>
                        <p className="text-xl font-bold text-green-600">{score}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <p className="text-[10px] uppercase font-bold text-gray-500">Total</p>
                        <p className="text-xl font-bold text-gray-600">{questions.length}</p>
                    </div>
                </div>
                
                <div className="flex gap-4 justify-center">
                    <button onClick={onExit} className="px-8 py-3 border border-gray-300 text-gray-600 rounded-xl font-bold text-xs uppercase hover:bg-gray-50 transition-colors">Voltar</button>
                    <button onClick={() => window.location.reload()} className="px-8 py-3 bg-[#20243B] text-white rounded-xl font-bold text-xs uppercase hover:bg-[#1a1e30] shadow-lg transition-transform hover:scale-105">Novo Treino</button>
                </div>
            </div>
        );
    }

    const currentQ = questions[currentIndex];
    const userAnswer = userAnswers[currentQ.id];
    const effectiveSelected = selectedOption !== null && selectedOption !== undefined ? selectedOption : userAnswer;
    const subjectInfo = ACADEMIA_CONSTANTS.SUBJECTS[currentQ.materia] || { label: currentQ.materia, color: 'gray', icon: 'fa-book' };
    
    // Análise de Status Normativo (V18.1)
    const isOutdated = currentQ.status_juridico === 'DESATUALIZADA' || currentQ.status_juridico === 'ANULADA';
    const normativeStatus = currentQ.normative_status || 'VIGENTE';

    return (
        <div className="max-w-5xl mx-auto animate-fade-in-up pb-32">
            {/* HEADER & FILTROS */}
            <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                     <button onClick={onExit} className="text-gray-400 hover:text-red-500 text-sm font-bold flex items-center gap-2 transition-colors">
                        <ArrowLeft className="w-4 h-4" /> ENCERRAR SESSÃO
                    </button>
                    <h2 className="text-lg font-bold text-[#20243B] uppercase tracking-wide flex items-center gap-2">
                        <i className="fas fa-stopwatch text-[#C5A059]"></i> Modo Treino
                    </h2>
                </div>
                {/* AdvancedFilterBar mantido, mas colapsado por padrão */}
                <AdvancedFilterBar onFilterChange={handleFilterUpdate} totalResults={questions.length} />
            </div>

            {/* CARD DA QUESTÃO */}
            <div className={`bg-white rounded-xl shadow-md border overflow-hidden relative ${isOutdated ? 'border-orange-300' : 'border-gray-300'}`}>
                
                {/* Barra de Progresso */}
                <div className="w-full bg-gray-100 h-1">
                    <div 
                        className="bg-[#C5A059] h-1 transition-all duration-500" 
                        style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                    ></div>
                </div>

                {/* --- FICHA TÉCNICA (30 CAMPOS) - PADRÃO OURO V18.1 --- */}
                {!isHeaderExpanded ? (
                    <div className="bg-slate-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 text-xs font-bold text-[#20243B]">
                                <span className="truncate">{subjectInfo.label}</span>
                                <span className="text-gray-300">|</span>
                                <span className="truncate">{currentQ.banca}</span>
                                <span className="text-gray-300">|</span>
                                <span className="shrink-0">{currentQ.year}</span>
                            </div>
                            <div className="text-[10px] text-gray-500 font-bold mt-1">Questão {currentIndex + 1} / {questions.length}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={toggleBookmark}
                                className={`text-gray-400 hover:text-[#C5A059] transition-all transform active:scale-90 ${isBookmarked ? 'text-[#C5A059]' : ''}`}
                                title={isBookmarked ? "Remover do Caderno" : "Salvar no Caderno de Erros"}
                            >
                                <Bookmark className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />
                            </button>
                            <button
                                onClick={() => setIsHeaderExpanded(true)}
                                className="text-[10px] font-black uppercase px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                            >
                                Ver Detalhes
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-slate-50 px-6 py-4 border-b border-gray-200 relative">
                        <div className="flex flex-wrap gap-2 items-center mb-3 pr-20">
                            <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase border flex items-center gap-1 ${currentQ.status_juridico === 'ANULADA' ? 'bg-red-100 text-red-700 border-red-200' : (currentQ.status_juridico === 'DESATUALIZADA' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-green-100 text-green-700 border-green-200')}`}>
                                <i className={`fas ${currentQ.status_juridico === 'ANULADA' ? 'fa-ban' : (currentQ.status_juridico === 'DESATUALIZADA' ? 'fa-history' : 'fa-check')}`}></i>
                                {currentQ.status_juridico || 'VÁLIDA'}
                            </span>
                            
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded bg-${subjectInfo.color}-100 text-${subjectInfo.color}-700 uppercase border border-${subjectInfo.color}-200 flex items-center gap-1`}>
                                <i className={`fas ${subjectInfo.icon}`}></i> {subjectInfo.label}
                            </span>
                            
                            <span className="text-[10px] font-mono text-gray-600 bg-white px-2 py-0.5 rounded border border-gray-300 shadow-sm flex items-center gap-1">
                                <span className="font-bold">{currentQ.banca}</span> • {currentQ.year} {currentQ.exam ? `• ${currentQ.exam}` : ''}
                            </span>

                            {currentQ.difficulty && (
                                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded border ${currentQ.difficulty === 'HARD' ? 'bg-red-50 text-red-600 border-red-100' : (currentQ.difficulty === 'EASY' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-yellow-50 text-yellow-600 border-yellow-100')}`}>
                                    {currentQ.difficulty}
                                </span>
                            )}
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-600 font-medium bg-white/60 p-2 rounded border border-gray-200 shadow-sm">
                            {currentQ.subject_macro && (
                                <div className="flex items-center gap-1">
                                    <span className="text-gray-400 uppercase font-bold text-[9px]">Tema:</span>
                                    <span className="text-gray-800">{currentQ.subject_macro}</span>
                                </div>
                            )}
                            {currentQ.subject_micro && (
                                <div className="flex items-center gap-1 border-l border-gray-300 pl-3">
                                    <span className="text-gray-400 uppercase font-bold text-[9px]">Subtema:</span>
                                    <span className="text-gray-800">{currentQ.subject_micro}</span>
                                </div>
                            )}
                            {normativeStatus !== 'VIGENTE' && (
                                <div className="flex items-center gap-1 border-l border-gray-300 pl-3">
                                    <AlertTriangle className="w-3 h-3 text-orange-500" />
                                    <span className="text-orange-600 font-bold uppercase">{normativeStatus}</span>
                                </div>
                            )}
                        </div>

                        <div className="absolute top-4 right-6 flex items-center gap-3">
                            <span className="text-xs font-bold text-gray-400">Questão {currentIndex + 1} <span className="text-gray-300">/</span> {questions.length}</span>
                            <div className="h-4 w-px bg-gray-300"></div>
                            <button 
                                onClick={toggleBookmark} 
                                className={`text-gray-400 hover:text-[#C5A059] transition-all transform active:scale-90 ${isBookmarked ? 'text-[#C5A059]' : ''}`} 
                                title={isBookmarked ? "Remover do Caderno" : "Salvar no Caderno de Erros"}
                            >
                                <Bookmark className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />
                            </button>
                            <button
                                onClick={() => setIsHeaderExpanded(false)}
                                className="text-[10px] font-black uppercase px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                )}

                <div className="p-8">
                    {/* Alerta de Questão Anulada/Desatualizada */}
                    {isOutdated && (
                        <div className="mb-6 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-3 text-xs text-orange-800">
                            <AlertTriangle className="w-5 h-5 text-orange-600" />
                            <div>
                                <p className="font-bold">Atenção: Questão {currentQ.status_juridico}</p>
                                <p className="opacity-80">O gabarito pode não refletir a jurisprudência atual. Use para fins históricos.</p>
                            </div>
                        </div>
                    )}

                    {/* Enunciado */}
                    <p className="text-gray-800 text-base leading-relaxed font-medium mb-8 selection:bg-[#C5A059] selection:text-white text-justify">
                        {currentQ.enunciado}
                    </p>

                    {/* Alternativas */}
                    <div className="space-y-3">
                        {(currentQ.alternatives || []).map((alt, idx) => {
                            const text = typeof alt === 'string' ? alt : alt.text;
                            const label = ['A','B','C','D'][idx];
                            
                            let btnClass = "border-gray-200 hover:bg-gray-50 hover:border-blue-300";
                            let icon = <span className="font-bold text-gray-400 w-6">{label})</span>;

                            // Lógica de Cores no Feedback
                            if (showFeedback) {
                                if (idx === currentQ.correta) {
                                    btnClass = "border-green-500 bg-green-50 ring-1 ring-green-500";
                                    icon = <Check className="w-5 h-5 text-green-600" />;
                                } else if (idx === userAnswer) {
                                    btnClass = "border-red-500 bg-red-50";
                                    icon = <X className="w-5 h-5 text-red-600" />;
                                } else {
                                    btnClass = "border-gray-100 opacity-50";
                                }
                            } else if (effectiveSelected === idx) {
                                // Estado Selecionado (antes do feedback, se houvesse delay)
                                btnClass = "border-blue-500 bg-blue-50 ring-1 ring-blue-500";
                            }

                            return (
                                <button 
                                    key={idx}
                                    onClick={() => handleAnswer(idx)}
                                    disabled={showFeedback}
                                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-start gap-3 group ${btnClass}`}
                                >
                                    <div className="shrink-0 pt-0.5">{icon}</div>
                                    <span className={`text-sm ${showFeedback && idx === currentQ.correta ? 'font-bold text-green-900' : 'text-gray-700'}`}>{text}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {!showFeedback && score === null && (
                    <div className="fixed bottom-0 left-0 right-0 z-40">
                        <div className="max-w-5xl mx-auto px-4 pb-4">
                            <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-2xl shadow-xl p-3 flex items-center justify-between gap-3">
                                <div className="text-xs font-bold text-gray-500">
                                    {effectiveSelected !== null && effectiveSelected !== undefined ? `Selecionada: ${['A','B','C','D'][effectiveSelected]}` : 'Selecione uma alternativa'}
                                </div>
                                <button
                                    onClick={confirmAnswer}
                                    disabled={effectiveSelected === null || effectiveSelected === undefined}
                                    className={`px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all border-b-4 ${effectiveSelected === null || effectiveSelected === undefined ? 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed' : 'bg-[#20243B] text-white border-black hover:bg-[#1a1e30] active:scale-95'}`}
                                >
                                    RESPONDER
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Área de Feedback (Engenharia Reversa) */}
                {showFeedback && (
                    <div className="bg-[#f8fafc] border-t-4 border-l-0 border-r-0 border-b-0 border-t-indigo-500 p-8 animate-fade-in-up">
                        <div className="flex items-start gap-5">
                            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center shrink-0 text-indigo-600 shadow-sm">
                                <Lightbulb className="w-6 h-6" />
                            </div>
                            <div className="flex-1">
                                <h4 className="text-sm font-black text-indigo-900 uppercase mb-2 tracking-wide">Engenharia Reversa</h4>
                                <p className="text-sm text-slate-700 leading-relaxed mb-4 border-l-2 border-indigo-200 pl-4 text-justify">
                                    {currentQ.justificativa}
                                </p>
                                
                                {/* Link Legal Rico */}
                                {currentQ.legal_meta && (
                                    <div className="flex flex-wrap gap-2 mt-4 items-center">
                                        <div className="flex items-center gap-2 text-xs font-bold text-white bg-indigo-600 px-3 py-1.5 rounded-lg shadow-sm cursor-help" title={currentQ.legal_meta.norm_name}>
                                            <Scale className="w-3 h-3" /> 
                                            {currentQ.legal_meta.norm_type}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs font-mono text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-1.5 rounded-lg">
                                            <BookOpen className="w-3 h-3" /> 
                                            Art. {currentQ.legal_meta.article}
                                        </div>
                                        {currentQ.legal_meta.link && (
                                            <a 
                                                href={currentQ.legal_meta.link} 
                                                target="_blank" 
                                                rel="noreferrer"
                                                className="text-xs text-blue-500 hover:text-blue-700 underline flex items-center gap-1 py-1.5 ml-2"
                                            >
                                                Ler na Fonte <i className="fas fa-external-link-alt"></i>
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="mt-8 flex justify-end">
                            <button 
                                onClick={nextQuestion} 
                                className="bg-[#20243B] text-white px-8 py-3 rounded-xl font-bold text-sm hover:bg-[#1a1e30] shadow-lg flex items-center gap-2 transition-transform active:scale-95 border-b-4 border-black"
                            >
                                {currentIndex < questions.length - 1 ? 'Próxima Questão' : 'Ver Resultado'} <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SimuladoEngine;
